<svelte:window on:keydown={handleKeydown} />

<AlertBox
  icon="delete"
  open={deleteCbModalOpen}
  heading="Remove content block?"
  text="Deleting this content block and its plugins (excluding named plugins) is permanent"
  cancelButton="No, keep block [ n ]"
  confirmButton="Yes, delete block [ y ]"
  on:cancel={closeDeleteCbModal}
  on:confirm={() => { handleDeleteBlock(contentBlockToDelete) }}
/>

{#if node}
  {#if ![COL_SHARED, COL_UNIQUE].includes(node.type)}
    <div style="color: red;"><code>Node component is of type "{node.type}"; only shared/unique column nodes are supported.</code></div>
  {:else if customisable}
    <div class="row">
      {#each nodeLayout as layout, i}
        {#each layout.rows as row, x}
          <div class={`${rowClasses(layout, x)} ${layoutClasses(node, layout, layoutHovering)}`}
            style={customStyles(node, layout)}
            data-style-sync={layout?.styleSyncID ?? 'Unset'}
            {...$$restProps}
            on:mouseenter={() => { layoutHovering = i }}
            on:mouseleave={() => { layoutHovering = null }}
          >
            {#if (layoutHovering === i || isMobile) && $store.editor.mode === modes.EDIT && !$store.editor.editPluginInline.instance}
              <div class="edit-nodes">
                <!-- Save node -->
                <div on:click={() => handleSaveBlock(node, i, layout.rows)} class="node-edit">
                  <div class="edit-button">
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M840-680v560H120v-720h560l160 160ZM480-240q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35ZM240-560h360v-160H240v160Z"/></svg>
                  </div>
                </div>
                <!-- Edit node -->
                <div on:click={() => handleEditBlock(node, i, layout.rows)} class="node-edit">
                  <div class="edit-button">
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M120-120v-170l585-583 167 171-582 582H120Zm584-528 56-56-56-56-56 56 56 56Z"/></svg>
                  </div>
                </div>
                <!-- Remove node-->
                <div on:click={() => openDeleteCbModal(node, i, layout.rows)} class="node-edit-options">
                  <div class="remove-button">
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM200-120v-600h-40v-80h200v-40h240v40h200v80h-40v600H200Z"/></svg>
                  </div>
                </div>
              </div>
              <!-- Add sections -->
              <div class="add-section add-section--top">
                <button on:click={() => handleOpenLayoutEditor(nodeLayout, node, i)} class='add-section__button'>
                  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z"/></svg>
                </button>
              </div>
              <div class="add-section add-section--bottom">
                <button on:click={() => handleOpenLayoutEditor(nodeLayout, node, i + 1)} class='add-section__button'>
                  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z"/></svg>
                </button>
              </div>
              <!-- Order sections -->
              <div class="edit-section-order top">
                <button on:click={() => handleReorderBlock(node, i)} class='add-section__button'>
                  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M440-160v-487L216-423l-56-57 320-320 320 320-56 57-224-224v487h-80Z"/></svg>
                </button>
              </div>
              <div class="edit-section-order bottom">
                <button on:click={() => handleReorderBlock(node, i + 1)} class='add-section__button'>
                  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor"><path d="M440-800v487L216-537l-56 57 320 320 320-320-56-57-224 224v-487h-80Z"/></svg>
                </button>
              </div>
            {/if}
            {#each row as col, c}
              <svelte:self
                shared={col.shared}
                animation={customAnimations(layout, col, c)}
                class={colClasses(layout, col, layoutHovering)}
                name={node.fields?.name + '-' + layout.name + '-' + col.name}
                layoutKey={layout.key}
              />
            {/each}
          </div>
        {/each}

        {#if layoutsAboveFold === i + 1}
          <Fold />
        {/if}
      {:else}
        {#if $store.editor.mode === modes.EDIT}
          <div class="add-section add-section--empty">
            <button on:click={() => handleOpenLayoutEditor(nodeLayout, node, 0)} class='add-section__button'>
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24"><path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z"/></svg>
            </button>
          </div>
        {/if}
      {/each}
    </div>
  {:else}
    <svelte:element
      this={element}
      bind:this={nodeEl}
      {...$$restProps}
      class={`
        node-element
        sortable-parent
        ${$$restProps.class}
        ${handleElementClass(isIntersecting)}
        ${$store.editor.mode === modes.EDIT || $store.editor.mode === modes.SORT || $store.editor.showEditStyle ? 'outline outline-2 outline-blue-400' : ''}
        ${$store.editor.mode === modes.EDIT || $store.editor.mode === modes.SORT || $store.editor.showEditStyle ? 'outline-dashed outline-1 outline-gray-300' : ''}
      `}
      class:editing={editing}
      data-node-id={node.id}
      data-block-id={template.childBlockId($store.render, node)}
      data-type={node.type}
      data-customisable={customisable}
      data-layout-name={node.fields?.name}
      data-animation-names={animation?.animations?.map(a => a.name).join(',')}
    >
      {#each template.nodes($store.render, node) as node (node.id)}
        {@const handle = `${node.id}-${handleNumber}`}
        {@const isEditing = (
          $store.editor.mode === modes.EDIT &&
          $store.editor.editPluginInline.component &&
          $store.editor.editPluginInline.id === node.pluginId &&
          $store.editor.editPluginInline.handle === handle
        )}
        <svelte:element
          this={$store.editor.mode === modes.VIEW && ($store.render.plugins[node.pluginId]?.fields['__jolojo-plugin-href__'] || node.fields?.href) ? 'a' : 'div'}
          href={$store.editor.mode === modes.VIEW && ($store.render.plugins[node.pluginId]?.fields['__jolojo-plugin-href__'] || node.fields?.href || null)}
          id={$store.render.plugins[node.pluginId]?.name ?? node.fields?.elementId}
          target={$store.editor.mode === modes.VIEW && (node.fields?.href || null) && (node.fields?.hrefTarget || null)}
          class="
            cms-plugin sortable-item relative
            {$store.editor.mode === modes.EDIT && !$store.render.plugins[node.pluginId]?.name ? 'hover-outline hover-outline-2 hover-outline-blue-500' : ''}
            {$store.editor.mode === modes.EDIT && $store.render.plugins[node.pluginId]?.name ? 'hover-outline hover-outline-2 hover-outline-green-500' : ''}
            {$store.editor.mode === modes.SORT && !$store.render.plugins[node.pluginId]?.name ? 'outline-dashed outline-2 outline-blue-400 cursor-grabbing' : ''}
            {$store.editor.mode === modes.SORT && $store.render.plugins[node.pluginId]?.name ? 'outline-dashed outline-2 outline-green-400 cursor-grabbing' : ''}
            {[modes.EDIT, modes.SORT, modes.DELETE].includes($store.editor.mode) && !isEditing ? 'block-plugin' : ''}
            {$store.editor.mode === modes.DELETE ? 'outline-dashed outline-1 outline-red-500 hover-outline hover-outline-2 cursor-pointer' : ''}
          "
          class:cms-plugin--editing-inline={isEditing}
          data-handle={handle}
          data-node-id={node.id}
          data-plugin-id={node.pluginId}
          data-plugin-ref={$store.render.plugins[node.pluginId]?.ref}
          on:click={e => nodeClick(e, node)}
        >
          {#if isEditing}
            <svelte:component
              this={$store.editor.editPluginInline.component}
              bind:this={$store.editor.editPluginInline.instance}
              pluginId={node.pluginId}
              {...template.pluginProps($store.render, node)}
            />
          {:else}
            <svelte:component
              this={template.plugin($store.render, node)}
              {...template.pluginProps($store.render, node)}
            />
          {/if}
        </svelte:element>
      {/each}
    </svelte:element>
  {/if}
{:else if !name}
  <div style="color: red;"><code>Node component is missing name attribute.</code></div>
{/if}

<script context="module">export const svelteCompilerVersion = {"major":4,"minor":2,"patch":18};
  let count = 0
</script>

<script>
  import { onMount, getContext } from 'svelte'
  import Fold from './Fold.svelte'
  import AlertBox from '@/components/core/extensions/alert_box/v1.0.0/AlertBox'

  export let element = 'div'
  export let name = ''
  export let shared = false
  export let customisable = false
  export let layoutKey = ''
  export let animation = {}
  export let layoutsAboveFold = 999

  animation.animations ||= []

  const platform = getContext('platform')
  const { store } = platform
  const { template } = platform.api.core
  const { modes } = platform.api.cms.editor

  const windowWidth = platform.api.core.store('window.width')

  const handleNumber = count
  const { COL_SHARED, COL_UNIQUE } = template

  let node = null
  let layoutHovering = null
  let editing = false
  let nodeLayout = []
  let isIntersecting = false
  let nodeEl = null

  let deleteCbModalOpen = false
  let contentBlockToDelete = {
    node: null,
    i: null,
    rows: [],
  }

  count++

  $: isMobile = $windowWidth < 992

  $: {
    node = findNode(name, layoutKey)
    editing = [modes.EDIT, modes.SORT].includes($store.editor.mode)

    nodeLayout = []
    if (customisable && Array.isArray(node?.fields?.layouts)) {
      for (const layout of node.fields.layouts) {
        if (typeof layout.layout === 'string') {
          const predefined = platform.api.core.copyObject($store.layouts.find(l => l.name === layout.layout))
          if (predefined) {
            predefined.key = layout.key
            predefined.styleSyncID ||= layout.styleSyncID
            nodeLayout.push(predefined)
          }
        } else if (Array.isArray(layout.layout)) {
          layout.layout.key = layout.key
          nodeLayout.push(layout.layout)
        }
      }
      nodeLayout = nodeLayout
    }

    onMount(() => {
      if (nodeEl) {
        // observers
        const elementIntersectionObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              isIntersecting = true
            }
          }
        }, {
          rootMargin: '-250px 0px',
        })
        elementIntersectionObserver.unobserve(nodeEl)
        elementIntersectionObserver.observe(nodeEl)
      }
    })

    const isLoggedIn = platform.api.core.isLoggedIn()

    // We only want to auto-push editor actions on the client-side
    // If the store is edited to be correct on the server-side then the
    // client-side won't know that anything needs to be done
    const { block } = $store.render.layout

    if (IS_BROWSER && isLoggedIn && block) {
      if (!node && name) {
        if (!block.nodes) {
          block.nodes = []
        }

        const newCol = {
          id: platform.api.core.uuid(),
          type: shared ? COL_SHARED : COL_UNIQUE,
          fields: { name, layoutKey },
          block: {},
        }

        const newColBlock = {
          id: platform.api.core.uuid(),
          nodes: [],
        }

        // Create a new block that we can link the new node to so it can hold content
        $store.render.blocks[newColBlock.id] = newColBlock
        $store.editor.actions.push({
          name: 'BLOCK_CREATE',
          meta: { blockId: newColBlock.id },
        })

        // Push a new column node onto the block we want to add it to
        block.nodes.push(newCol)
        $store.editor.actions.push({
          name: 'NODE_CREATE',
          meta: {
            blockId: block.id,
            nodeId: newCol.id,
            type: newCol.type,
            fields: newCol.fields,
          },
        })

        // Link the new node with the new block
        $store.render.links[newCol.id] = newColBlock.id
        $store.editor.actions.push({
          name: 'LINK_SET',
          meta: {
            nodeId: newCol.id,
            blockId: newColBlock.id,
          },
        })

        $store.editor.actions = $store.editor.actions
        $store.render = $store.render

        node = newCol
      }

      if (node) {
        const blockId = template.childBlockId($store.render, node)
        const block = template.findBlock($store.render, blockId)

        if (!Array.isArray(block.nodes)) {
          block.nodes = []
        }

        const shouldBeShared = node.type === COL_UNIQUE && shared
        const shouldBeUnique = node.type === COL_SHARED && !shared

        if (shouldBeShared || shouldBeUnique) {
          node.type = node.type === COL_SHARED ? COL_UNIQUE : COL_SHARED
          $store.editor.actions.push({
            name: 'NODE_SET_TYPE',
            meta: {
              nodeId: node.id,
              type: node.type,
            },
          })

          $store.editor.actions = $store.editor.actions
          $store.render = $store.render
        }
      }
    }
  }

  function customAnimations (layout, col, c) {
    const nodeLayout = node.fields.layouts.find(l => l.key === layout.key)
    const customAnimation = nodeLayout?.animationNames?.find(a => a.col === c)

    if (!col.animation) {
      col.animation = { animations: [] }
    }

    if (customAnimation) {
      col.animation.animations.push({ name: customAnimation.name, type: 'custom' })
    }

    return col.animation
  }

  function customStyles (node, layout) {
    let contentBlockFontColour = node?.fields?.layouts?.find(f => f.key === layout.key)?.fontColour || 'inherit'
    let contentBlockBackgroundColour = node?.fields?.layouts?.find(f => f.key === layout.key)?.backgroundColour
    let contentBlockBackgroundImage = node?.fields?.layouts?.find(f => f.key === layout.key)?.backgroundImage
    let contentBlockBackgroundRepeat = node?.fields?.layouts?.find(f => f.key === layout.key)?.backgroundRepeat

    if (layout?.styleSyncID) {
      const payload = $store.render.styleSyncIDs[layout?.styleSyncID]?.payload
      if (payload) {
        contentBlockFontColour = payload.fontColour || 'inherit'
        contentBlockBackgroundColour = payload.backgroundColour
        contentBlockBackgroundImage = payload.backgroundImage
        contentBlockBackgroundRepeat = payload.backgroundRepeat
      }
    }

    return [
      `color: ${contentBlockFontColour};`,
      contentBlockBackgroundColour ? `background: ${contentBlockBackgroundColour};` : '',
      contentBlockBackgroundImage ? `
          background-image: url(${contentBlockBackgroundImage}?size=xxl);
          background-repeat: ${contentBlockBackgroundRepeat || 'no-repeat'};
          background-size: ${contentBlockBackgroundRepeat === 'repeat' ? 'contain' : 'cover'};
          background-position: center;
        ` : '',
    ].join('\n')
  }

  function layoutClasses (node, layout, layoutHovering) {
    const editSort = $store.editor.mode === modes.EDIT || $store.editor.mode === modes.SORT
    const classes = ['layout']

    let contentBlockClasses = node?.fields?.layouts?.find(f => f.key === layout.key)?.classNames?.map(f => f?.class)?.join(' ')?.trim() || ''
    if (layout?.styleSyncID) {
      const payload = $store.render.styleSyncIDs[layout?.styleSyncID]?.payload
      if (payload) {
        contentBlockClasses = payload.classNames?.map(f => f?.class)?.join(' ')?.trim() || ''
      }
    }

    if ($$restProps.class) {
      classes.push($$restProps.class)
    }

    if (Array.isArray(layout.layoutClassNames)) {
      classes.push(...layout.layoutClassNames)
    }

    if (contentBlockClasses) {
      classes.push(contentBlockClasses)
    }

    if (Array.isArray(node?.fields?.classNames)) {
      classes.push(node.fields.classNames.map(f => f.class).join(' '))
    }

    if (editSort) {
      if (layoutHovering) {
        classes.push('layout--edit-hover')
      } else {
        classes.push('layout--edit')
      }
    }

    return classes.join(' ')
  }

  function rowClasses (layout, x) {
    const classes = ['row']

    if (Array.isArray(layout.rowClassNames?.[0])) {
      classes.push(...layout.rowClassNames[x])
    } else if (Array.isArray(layout.rowClassNames)) {
      classes.push(...layout.rowClassNames)
    }

    if (Array.isArray(layout.classNames)) {
      classes.push(...layout.classNames)
    }

    return classes.join(' ')
  }

  function colClasses (layout, col, layoutHovering) {
    const edit = $store.editor.mode === modes.EDIT
    const classes = ['col']

    if (Array.isArray(layout.rowClassNames)) {
      classes.push(...layout.rowClassNames)
    }

    if (Array.isArray(col.classNames)) {
      classes.push(...col.classNames)
    }

    if (edit) {
      if (layoutHovering) {
        classes.push('col--edit-hover')
      } else {
        classes.push('col--edit')
      }
    }

    return classes.join(' ')
  }

  async function nodeClick (e, node) {
    if ($store.editor.mode !== modes.VIEW) {
      if (e.target.tagName !== 'INPUT' || e.target.getAttribute('type') !== 'color') {
        e.preventDefault()
      }
      if (!e.target.dataset.nodeId) {
        e.stopPropagation()
        e.target.closest('[data-node-id]')?.click()

        return
      }

      if (node.id === e.target.dataset.nodeId) {
        $store.editor.node = node
      }
    }
  }

  function findNode (name, layoutKey) {
    if (!name) {
      return null
    }

    const node = template?.findNode($store.render, node => {
      if (layoutKey) {
        return node.fields?.layoutKey === layoutKey && node.fields?.name === name
      }

      return node.fields?.name === name
    })

    if (!node) {
      return null
    }

    if (customisable && node.fields && !shared) {
      node.fields.styleSyncID = $store.render.nodeFields[node.id]?.styleSyncID
      node.fields.layouts = $store.render.nodeFields[node.id]?.layouts
      node.fields.classNames = $store.render.nodeFields[node.id]?.classNames
    }

    return node
  }

  function handleOpenLayoutEditor (layout, node, index) {
    $store.editor.editingNode = node
    $store.editor.layoutToEdit = layout
    $store.editor.currentIndex = index
    platform.api.core.dispatch('set.sidebarMode', 'contentBlocks')

    $store.editor.editingSection = {
      node,
      index,
    }
  }

  function handleElementClass (isIntersecting) {
    if ($store.editor.mode === 'EDIT' || $store.editor.mode === 'SORT') {
      return ''
    }

    const classNames = ['cms-plugins', 'sortable-parent', $$restProps.class]

    if (Array.isArray(animation?.animations)) {
      if (animation.animations.length) {
        classNames.push('transition-ease', 'transform')
      }
      for (const ani of animation.animations) {
        if (!platform.animations[ani.name]) {
          continue
        }
        if (isIntersecting) {
          classNames.push(...platform.animations[ani.name].endingClassNames)
        } else {
          classNames.push(...platform.animations[ani.name].startingClassNames)
        }
      }
    }

    return [...new Set(classNames.filter(c => c))].join(' ')
  }

  function openDeleteCbModal (node, i, rows) {
    contentBlockToDelete = {
      node,
      i,
      rows,
    }

    deleteCbModalOpen = true
  }

  function closeDeleteCbModal () {
    contentBlockToDelete = {
      node: null,
      i: null,
      rows: [],
    }

    deleteCbModalOpen = false
  }

  function handleDeleteBlock ({ node, i, rows }) {
    const plugins = findPluginsInContentBlock(node, i, rows)

    for (const p of plugins) {
      removePlugin(p)
    }

    node.fields.layouts.splice(i, 1)
    node.fields.layouts = node.fields.layouts
    setContentBlock()

    closeDeleteCbModal()
  }

  function handleEditBlock (node, i, rows) {
    $store.editor.showSectionEditor = true
    $store.editor.editingSection = {
      node,
      index: i,
      rows,
    }
  }

  async function handleSaveBlock (node, i, rows) {
    const nodeName = node.fields.name
    const layoutName = node.fields.layouts[i].layout
    const fields = structuredClone(node.fields.layouts[i])
    const r = rows[0]

    const cbColumns = []
    for (const row of r) {
      const cbNode = $store.render.layout.block.nodes.find(n =>
        n.fields.name === `${nodeName}-${layoutName}-${row.name}` &&
          n.fields.layoutKey === node.fields.layouts[0].key,
      )
      if (cbNode) {
        cbColumns.push(cbNode)
      }
    }

    const plugins = findPluginsInContentBlock(node, i, rows)

    const pluginInfo = []
    for (const plugin of plugins) {
      if (!plugin.name) {
        const updatedPlugin = platform.api.core.copyObject(plugin)
        updatedPlugin.name = '__jolojo_generated__' + platform.api.core.uuid()
        await platform.api.cms.plugins.update(updatedPlugin)
      }

      pluginInfo.push({
        ref: plugin.ref,
        fields: JSON.stringify(plugin.fields),
        nodeName: plugin.nodeName,
        // pluginName field is kept in for backwards compatability...
        pluginName: plugin.name,
        pluginID: plugin.id,
      })
    }

    delete fields.key

    $store.editor.customContentBlocks = {}

    $store.editor.customContentBlocks.content = {
      contentBlock: {
        nodeName,
        layoutName,
        fields: {
          styleSyncID: platform.api.core.uuid(),
          ...fields,
        },
      },
      plugins: pluginInfo,
    }

    $store.editor.showSaveContentBlockEditor = true
  }

  function handleReorderBlock (node, i) {
    if (i === 0) { return }
    if (i === node.fields.layouts.length) { return }

    const layout = node.fields.layouts.splice(i, 1)[0]
    node.fields.layouts.splice(i - 1, 0, layout)
    node.fields.layouts = node.fields.layouts
    setContentBlock()
  }

  function findPluginsInContentBlock (node, i, rows) {
    const nodeName = node.fields.name
    const layoutName = node.fields.layouts[i].layout

    const cbColumns = []
    for (const row of rows[0]) {
      const cbNode = $store.render.layout.block.nodes.find(n =>
        n.fields.name === `${nodeName}-${layoutName}-${row.name}` &&
          n.fields.layoutKey === node.fields.layouts[i].key,
      )
      if (cbNode) {
        cbColumns.push(cbNode)
      }
    }

    const plugins = []
    for (const cbCol of cbColumns) {
      const linkId = $store.render.links[cbCol.id]
      const block = $store.render.blocks[linkId]

      for (const cbNode of block.nodes) {
        if (cbNode.type === 'plugin') {
          const plugin = $store.render.plugins[cbNode.pluginId]
          plugins.push({
            ...plugin,
            nodeName: cbCol.fields.name,
            nodeId: cbNode.id,
          })
        }
      }
    }

    return plugins
  }

  function removePlugin (plugin) {
    let pluginNode = document.querySelector(`[data-node-id="${plugin.nodeId}"]`)

    while (!pluginNode.classList || !pluginNode.classList.contains('cms-plugin')) {
      if (!pluginNode || pluginNode === document.body) {
        return
      }

      pluginNode = pluginNode.parentNode
    }

    const blockNode = pluginNode.parentNode
    const blockId = blockNode.dataset.blockId
    const pluginId = pluginNode.dataset.pluginId
    const index = Array.from(blockNode.children).indexOf(pluginNode)

    const block = template.findBlock($store.render, blockId)

    block.nodes.splice(index, 1)

    const newPlugin = $store.editor.actions.find(a => a.name === 'PLUGIN_CREATE' && a.meta.pluginId === pluginId)

    if (newPlugin) {
      $store.editor.actions.splice($store.editor.actions.indexOf(newPlugin), 1)

      const blockAddPluginNode = $store.editor.actions.find(a => a.name === 'BLOCK_ADD_PLUGIN_NODE' && a.meta.pluginId === pluginId)
      const pluginUpdate = $store.editor.actions.find(a => a.name === 'PLUGIN_UPDATE' && a.meta.pluginId === pluginId)

      if (blockAddPluginNode) {
        $store.editor.actions.splice($store.editor.actions.indexOf(blockAddPluginNode), 1)
      }
      if (pluginUpdate) {
        $store.editor.actions.splice($store.editor.actions.indexOf(pluginUpdate), 1)
      }
    } else {
      $store.editor.actions.unshift({
        name: 'BLOCK_REMOVE_NODE',
        meta: {
          blockId,
          index,
        },
      })
    }

    $store.editor.actions = $store.editor.actions
    $store.editor = $store.editor
    $store.render = $store.render
    $store.render.key = Math.random()
  }

  function setContentBlock () {
    for (const layout of node.fields.layouts) {
      if (!layout.key) {
        const nextKey = (Math.max(0, ...node.fields.layouts.map(l => Number.isNaN(Number(l.key)) ? 0 : Number(l.key))) + 1).toString()
        layout.key = nextKey
      }
    }

    if (!node) { return }

    if (node.type === template.COL_UNIQUE) {
      $store.render.nodeFields[node.id] = $store.render.nodeFields[node.id] || {}
      $store.render.nodeFields[node.id].layouts = node.fields.layouts
    }

    $store.editor.actions.push({
      name: 'NODE_SET_LAYOUTS',
      meta: {
        layouts: node.fields.layouts,
        nodeId: node.id,
        source: 'v2.0.0/Node.svelte: setContentBlock',
      },
    })

    $store.render = $store.render
  }

  function handleKeydown (event) {
    if (!deleteCbModalOpen || event.repeat) {
      return
    }

    if (event.code === 'KeyY') {
      handleDeleteBlock(contentBlockToDelete)
    }

    if (event.code === 'KeyN' || event.code === 'Escape') {
      closeDeleteCbModal()
    }
  }
</script>

<style lang="scss">:global(.block-plugin *) {
  pointer-events: none !important;
}

.cms-plugin {
  display: block;
}

.editing {
  min-height: 81px;
  min-width: 81px;
}

.transition-ease {
  transition: all 0.6s cubic-bezier(0.65, 0, 0.35, 1);
}

.row {
  position: relative;
}

.col--edit-hover {
  outline: dashed 1px #707070;
}

.layout {
  transition: all;
  position: relative;
}

.layout--edit {
  outline: dashed 1px #707070;
}

.layout--edit-hover {
  outline: solid 1px #707070;
}

.edit-nodes {
  display: flex;
  position: absolute;
  right: 0px;
  top: 0px;
  background-color: rgb(242, 242, 242);
  overflow: hidden;
  border-bottom-left-radius: 14px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.4);
  z-index: 100;
}

.remove-button,
.node-edit {
  cursor: pointer;
  color: #707070;
  background: rgb(242, 242, 242);
  padding: 8px 16px;
}
.remove-button svg,
.node-edit svg {
  width: 24px;
  height: 24px;
}
@media screen and (max-width: 650px) {
  .remove-button,
.node-edit {
    font-size: 8px;
    padding: 4px 8px;
  }
  .remove-button svg,
.node-edit svg {
    height: 18px;
    width: 18px;
  }
}

.remove-button:hover,
.node-edit:hover {
  filter: brightness(0.9);
}

.remove-button {
  color: #707070;
  background-color: rgb(242, 242, 242);
}

.edit-section-order {
  position: absolute;
  left: 0px;
  z-index: 200;
}
.edit-section-order.top {
  top: 0px;
}
.edit-section-order.top button {
  border-bottom-right-radius: 8px;
}
.edit-section-order.bottom {
  bottom: 0px;
}
.edit-section-order.bottom button {
  border-top-right-radius: 8px;
}
.edit-section-order button {
  padding: 8px 16px;
}
@media screen and (max-width: 650px) {
  .edit-section-order button {
    font-size: 8px;
    padding: 4px 12px;
  }
  .edit-section-order button svg {
    height: 15px;
    width: 15px;
  }
}

.add-section {
  position: absolute;
  display: flex;
  justify-content: center;
  z-index: 200;
  left: 50%;
  transform: translate(-50%, 0);
}

.add-section--empty {
  position: relative;
  margin: 0 auto;
}

.add-section--top {
  top: 0px;
}
.add-section--top .add-section__button {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  overflow: hidden;
}

.add-section--bottom {
  bottom: 0px;
  margin: auto;
}
.add-section--bottom .add-section__button {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  overflow: hidden;
}

.add-section__button {
  font-size: 12px;
  text-transform: uppercase;
  background: rgb(242, 242, 242);
  color: #707070;
  fill: #707070;
  border: none;
  padding: 8px 40px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.4);
}
.add-section__button:hover {
  filter: brightness(0.9);
}
@media screen and (max-width: 650px) {
  .add-section__button {
    font-size: 8px;
    padding: 4px 12px;
  }
  .add-section__button svg {
    height: 18px;
    width: 18px;
  }
}

.text-left {
  text-align: left;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

.items-start {
  align-items: flex-start;
}

.items-center {
  align-items: center;
}

.items-end {
  align-items: flex-end;
}

.my-0 {
  margin-top: 0px;
  margin-bottom: 0px;
}

.my-2 {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.my-4 {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.my-8 {
  margin-top: 2rem;
  margin-bottom: 2rem;
}

.my-12 {
  margin-top: 3rem;
  margin-bottom: 3rem;
}

.my-16 {
  margin-top: 4rem;
  margin-bottom: 4rem;
}

.my-24 {
  margin-top: 6rem;
  margin-bottom: 6rem;
}

.my-32 {
  margin-top: 8rem;
  margin-bottom: 8rem;
}

@media (max-width: 650px) {
  .my-8, .my-12, .my-16, .my-24, .my-32 {
    margin-top: 2rem;
    margin-bottom: 2rem;
  }
}

.px-0 {
  padding-left: 0px;
  padding-right: 0px;
}

.px-4 {
  padding-left: 1rem;
  padding-right: 1rem;
}

.px-8 {
  padding-left: 2rem;
  padding-right: 2rem;
}

.px-16 {
  padding-left: 4rem;
  padding-right: 4rem;
}

.px-24 {
  padding-left: 6rem;
  padding-right: 6rem;
}

.px-32 {
  padding-left: 8rem;
  padding-right: 8rem;
}

@media (max-width: 650px) {
  .px-8, .px-16, .px-24, .px-32 {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

.py-0 {
  padding-top: 0px;
  padding-bottom: 0px;
}

.py-8 {
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.py-16 {
  padding-top: 4rem;
  padding-bottom: 4rem;
}

.py-24 {
  padding-top: 6rem;
  padding-bottom: 6rem;
}

.py-32 {
  padding-top: 8rem;
  padding-bottom: 8rem;
}

@media (max-width: 650px) {
  .py-8, .py-16, .py-24, .py-32 {
    padding-top: 2rem;
    padding-bottom: 2rem;
  }
}

.max-w-full {
  max-width: 100%;
}

.outline-dashed {
  outline-style: dashed;
}

.outline-1 {
  outline-width: 1px;
}

.outline-2 {
  outline-width: 2px;
}

.outline-red-500 {
  outline-color: #EF4444;
}

.outline-blue-400 {
  outline-color: #60A5FA;
}

.outline-green-400 {
  outline-color: #41de80;
}

.cursor-grabbing {
  cursor: grabbing;
}

.hover-outline:hover {
  outline-style: solid;
}

.hover-outline-2:hover {
  outline-width: 2px;
}

.hover-outline-blue-500:hover {
  outline-color: #3B82F6;
}

.hover-outline-green-500:hover {
  outline-color: #41de80;
}

.outline-gray-300 {
  outline-color: #D1D5DB;
}

.opacity-0 {
  opacity: 0;
}

.opacity-1 {
  opacity: 1;
}

.translate-y-0 {
  transform: translateY(0px);
}

.translate-y-20 {
  transform: translateY(5rem);
}

.-translate-y-20 {
  transform: translateY(-5rem);
}

.translate-x-0 {
  transform: translateX(0px);
}

.translate-x-20 {
  transform: translateX(5rem);
}

.-translate-x-20 {
  transform: translateX(-5rem);
}

.scale-100 {
  transform: scale(1);
}

.scale-0 {
  transform: scale(0);
}</style>
